import { Injectable } from '@angular/core';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { mergeMap, map, catchError, tap, withLatestFrom, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  AdminLoadOrganizations,
  AdminSetOrganizationError,
  AdminLoadCurrentOrganization,
  AdminSetCurrentOrganization,
  AdminCreateOrganization,
  AdminOpenOrganization,
  AdminDeleteOrganization,
  AdminHardDeleteOrganization,
  AdminOpenOrganizations,
  AdminUpdateOrganization,
  AdminSetOrganizationsCsv,
  AdminSetOrganizationsCsvError,
  AdminLoadOrganizationsCsv,
  AdminIncludedDeletedOrganizationsChanged,
  AdminOrganizationPageChanged,
  AdminOrganizationsLoaded,
  AdminOrganizationSortChanged,
  AdminOrganizationsSearchChanged,
  AdminOrganizationEffectActions,
  AdminOrganizationListActions,
} from './admin-organization.actions';
import { AdminOrganizationService } from './admin-organization.service';
import { AdminOrganization } from 'src/app/admin/models/organization.model';
import { Router } from '@angular/router';
import { DownLoadFileService } from 'src/app/shared/services/download-file.service';
import { AppStore } from '../store.model';
import { Store } from '@ngrx/store';
import { getAdminOrganizationQuery } from './admin-organization.selectors';
import {
  AdminOrganizationApiError,
  AdminOrganizationApiErrorTypes,
} from './admin-organization.errors';
import { AdminOrganizationSubscriptionEffectActions } from '../admin-organization-subscription/admin-organization-subscription.actions';

@Injectable()
export class AdminOrganizationEffects {
  loadAdminOrganizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdminLoadOrganizations,
        AdminOrganizationPageChanged,
        AdminOrganizationSortChanged,
        AdminIncludedDeletedOrganizationsChanged,
        AdminOrganizationsSearchChanged,
        AdminOrganizationEffectActions.undeployAllProjectsInOrganizationSucceeded,
      ),
      withLatestFrom(this.store.select(getAdminOrganizationQuery)),
      switchMap(([_, query]) =>
        this.adminOrganizationService.loadAdminOrganizations(query).pipe(
          map(({ data, metadata }) =>
            AdminOrganizationsLoaded({ organizations: data, total: metadata.total }),
          ),
          catchError((error) => of(AdminSetOrganizationError({ error }))),
        ),
      ),
    ),
  );

  loadAdminOrganizationsCsv$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminLoadOrganizationsCsv),
      withLatestFrom(this.store.select(getAdminOrganizationQuery)),
      mergeMap(([, { includeDeleted }]) =>
        this.adminOrganizationService.loadAdminOrganizationsCsv({ includeDeleted }).pipe(
          map((blob: Blob) => {
            this.downloadFileService.blobToFile(
              blob,
              'application/octet-stream',
              `organizations-${new Date().toISOString()}.csv`,
            );
            return AdminSetOrganizationsCsv();
          }),
          catchError((error) => of(AdminSetOrganizationsCsvError({ error }))),
        ),
      ),
    ),
  );

  getAdminOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        AdminLoadCurrentOrganization,
        AdminOrganizationSubscriptionEffectActions.updateSubscriptionSucceeded,
      ),
      mergeMap(({ id }) =>
        this.adminOrganizationService.getAdminOrganization(id).pipe(
          map((organization: AdminOrganization) => AdminSetCurrentOrganization({ organization })),
          catchError((error) => of(AdminSetOrganizationError({ error }))),
        ),
      ),
    ),
  );

  addAdminOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminCreateOrganization),
      mergeMap(
        ({ name, displayName, description, organizationType, addDefaultConfig, addStockDPS }) =>
          this.adminOrganizationService
            .addAdminOrganization(
              name,
              displayName,
              description,
              organizationType,
              addDefaultConfig,
              addStockDPS,
            )
            .pipe(
              map((organization: AdminOrganization) =>
                AdminSetCurrentOrganization({ organization }),
              ),
              catchError((error) => of(AdminSetOrganizationError({ error }))),
            ),
      ),
    ),
  );

  updateAdminOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminUpdateOrganization),
      mergeMap(({ id, organization }) =>
        this.adminOrganizationService.updateAdminOrganization(id, organization).pipe(
          map(() => AdminLoadCurrentOrganization({ id })),
          catchError((error) => of(AdminSetOrganizationError({ error }))),
        ),
      ),
    ),
  );

  openOrganization$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminOpenOrganization),
        tap((action) => this.router.navigate(['/admin', 'organizations', action.id])),
      ),
    { dispatch: false },
  );

  deleteAdminOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminDeleteOrganization),
      mergeMap(({ id }) =>
        this.adminOrganizationService.deleteAdminOrganization(id).pipe(
          mergeMap(() => [AdminLoadOrganizations(), AdminOpenOrganizations()]),
          catchError((error) => of(AdminSetOrganizationError({ error }))),
        ),
      ),
    ),
  );

  hardDeleteAdminOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminHardDeleteOrganization),
      mergeMap(({ id }) =>
        this.adminOrganizationService.hardDeleteAdminOrganization(id).pipe(
          mergeMap(() => [AdminLoadOrganizations(), AdminOpenOrganizations()]),
          catchError((error) => of(AdminSetOrganizationError({ error }))),
        ),
      ),
    ),
  );

  startUndeployAllProjectsInOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminOrganizationListActions.undeployAllProjectsConfirmed),
      switchMap(({ organizationId }) =>
        this.adminOrganizationService.undeployAllProjectsInOrganization(organizationId).pipe(
          map((task) =>
            AdminOrganizationEffectActions.undeployAllProjectsInOrganizationStarted({ task }),
          ),
          catchError((error) =>
            of(
              AdminOrganizationEffectActions.undeployAllProjectsInOrganizationErrored({
                error: new AdminOrganizationApiError(
                  AdminOrganizationApiErrorTypes.AdminUndeployAllProjectsInOrganizationError,
                  error,
                ),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  pollUndeployAllProjectsInOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AdminOrganizationEffectActions.undeployAllProjectsInOrganizationStarted),
      switchMap(({ task }) =>
        this.adminOrganizationService.pollUndeployAllProjectsInOrganizationStatus(task.id).pipe(
          map(() => AdminOrganizationEffectActions.undeployAllProjectsInOrganizationSucceeded()),
          catchError((error) =>
            of(
              AdminOrganizationEffectActions.undeployAllProjectsInOrganizationErrored({
                error,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  openOrganizations$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AdminOpenOrganizations),
        tap(() => this.router.navigate(['/admin', 'organizations'])),
      ),
    { dispatch: false },
  );

  constructor(
    private actions$: Actions,
    private adminOrganizationService: AdminOrganizationService,
    private router: Router,
    private downloadFileService: DownLoadFileService,
    private store: Store<AppStore>,
  ) {}
}
